package dev.mobilearchitecture.web.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.*
import dev.mobilearchitecture.web.toSitePalette
import com.varabyte.kobweb.compose.css.boxShadow
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.LineStyle
import dev.mobilearchitecture.web.pages.CardStyle
import dev.mobilearchitecture.web.pages.SectionContainerStyle
import dev.mobilearchitecture.web.PrimaryButtonVariant
import dev.mobilearchitecture.web.components.widgets.IconButton
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns

val SessionsGridStyle = CssStyle.base {
    Modifier
        .gap(1.5.cssRem)
        .fillMaxWidth()
        .margin(top = 0.5.cssRem)
}

val SessionCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .height(100.percent)
            .padding(2.cssRem)
            .borderRadius(1.cssRem)
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgba(255, 255, 255, 0.05f)) // glass card bg
            .styleModifier { property("backdrop-filter", "blur(10px)") }
            .border(1.px, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgba(255, 255, 255, 0.1f)) // glass card border
            .boxShadow(offsetX = 0.px, offsetY = 4.px, blurRadius = 6.px, spreadRadius = (-1).px, color = com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0.1f))
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .justifyContent(JustifyContent.SpaceBetween)
            .transition(Transition.of("transform", 250.ms), Transition.of("box-shadow", 250.ms), Transition.of("border-color", 250.ms))
    }
    cssRule(":hover") {
        Modifier
            .scale(1.02)
            .boxShadow(offsetX = 0.px, offsetY = 10.px, blurRadius = 15.px, spreadRadius = (-3).px, color = com.varabyte.kobweb.compose.ui.graphics.Color.rgba(255, 255, 255, 0.2f))
            .border(1.px, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgba(255, 255, 255, 0.3f))
    }
}

val MostRecommendedCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .height(100.percent)
            .padding(2.cssRem)
            .borderRadius(1.cssRem)
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgba(236, 72, 153, 0.05f)) // Light pink bg
            .styleModifier { property("backdrop-filter", "blur(10px)") }
            .boxShadow(offsetX = 0.px, offsetY = 4.px, blurRadius = 6.px, spreadRadius = (-1).px, color = com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0.1f))
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .justifyContent(JustifyContent.SpaceBetween)
            .transition(Transition.of("transform", 250.ms), Transition.of("box-shadow", 250.ms), Transition.of("border-color", 250.ms))
            .position(Position.Relative)
            .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
            .border(1.px, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgba(236, 72, 153, 0.5f)) // Pink border
    }
    cssRule(":hover") {
        Modifier
            .scale(1.02)
            .boxShadow(offsetX = 0.px, offsetY = 10.px, blurRadius = 15.px, spreadRadius = (-3).px, color = com.varabyte.kobweb.compose.ui.graphics.Color.rgba(255, 255, 255, 0.2f))
    }
}

val MostRecommendedBadgeStyle = CssStyle.base {
    Modifier
        .position(Position.Absolute)
        .top(0.px)
        .right(0.px)
        .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xec4899)) // Pink
        .color(Colors.White)
        .fontSize(0.6.cssRem)
        .fontWeight(FontWeight.Bold)
        .padding(topBottom = 0.25.cssRem, leftRight = 0.75.cssRem)
        .styleModifier {
            property("border-bottom-left-radius", "0.5rem")
            property("text-transform", "uppercase")
            property("letter-spacing", "-0.05em")
        }
}

@Composable
fun SessionOptions() {
    Column(Modifier.fillMaxWidth().maxWidth(1000.px).padding(top = 0.5.cssRem, bottom = 2.cssRem)) {
        SimpleGrid(numColumns(1, md = 3), SessionsGridStyle.toModifier()) {
            // 15-Min Quick Sync
            Box(SessionCardStyle.toModifier().onClick { kotlinx.browser.window.open("https://mobilearchitecture.setmore.com/services", "_blank") }.cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)) {
                Column(Modifier.fillMaxSize()) {
                    H4(attrs = { style { property("color", "#FFFFFF"); property("margin-top", "0"); property("margin-bottom", "0.5rem"); property("font-size", "1.25rem") } }) {
                        Text("15-Min Quick Sync")
                    }
                    P(attrs = { style { property("margin-bottom", "1.5rem"); property("color", "#CBD5E1"); property("font-size", "0.875rem") } }) {
                        Text("Perfect for a quick code review, getting unblocked on a specific bug, or career advice.")
                    }
                    // push button to bottom
                    Box(Modifier.flexGrow(1))
                    Button(
                        onClick = {
                            // Event handled by parent Box onClick to prevent double opening tabs
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .backgroundColor(Colors.Transparent)
                            .color(Colors.White)
                            .border(1.px, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgba(255, 255, 255, 0.2f))
                            .styleModifier { property("transition", "all 0.2s"); property("pointer-events", "none") }
                    ) {
                        Text("Book Quick Sync")
                    }
                }
            }

            // 30-Min Deep Dive
            Box(SessionCardStyle.toModifier().onClick { kotlinx.browser.window.open("https://mobilearchitecture.setmore.com/services", "_blank") }.cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)) {
                Column(Modifier.fillMaxSize()) {
                    H4(attrs = { style { property("color", "#FFFFFF"); property("margin-top", "0"); property("margin-bottom", "0.5rem"); property("font-size", "1.25rem") } }) {
                        Text("30-Min Deep Dive")
                    }
                    P(attrs = { style { property("margin-bottom", "1.5rem"); property("color", "#CBD5E1"); property("font-size", "0.875rem") } }) {
                        Text("Architecture consultation, SDK design review, or roadmap planning for your mobile project.")
                    }
                    Box(Modifier.flexGrow(1))
                    Button(
                        onClick = {
                            // Event handled by parent Box onClick
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .backgroundColor(Colors.Transparent)
                            .color(Colors.White)
                            .border(1.px, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgba(255, 255, 255, 0.2f))
                            .styleModifier { property("transition", "all 0.2s"); property("pointer-events", "none") }
                    ) {
                        Text("Book Consultation")
                    }
                }
            }

            // 90-Min Mock Interview
            Box(MostRecommendedCardStyle.toModifier().onClick { kotlinx.browser.window.open("https://mobilearchitecture.setmore.com/services", "_blank") }.cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)) {
                Box(MostRecommendedBadgeStyle.toModifier()) {
                    Text("Most Recommended")
                }
                Column(Modifier.fillMaxSize()) {
                    H4(attrs = { style { property("color", "#FFFFFF"); property("margin-top", "0"); property("margin-bottom", "0.5rem"); property("font-size", "1.25rem") } }) {
                        Text("90-Min Mock Interview")
                    }
                    P(attrs = { style { property("margin-bottom", "1.5rem"); property("color", "#CBD5E1"); property("font-size", "0.875rem") } }) {
                        Text("The full experience: 60m interview, 20m debrief, and a detailed written feedback report.")
                    }
                    Box(Modifier.flexGrow(1))
                    Button(
                        onClick = {
                            // Event handled by parent Box onClick
                        },
                        modifier = Modifier
                            .fillMaxWidth()
                            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xec4899)) // Pink
                            .color(Colors.White)
                            .styleModifier { property("transition", "all 0.2s"); property("pointer-events", "none") }
                    ) {
                        Text("Book Interview")
                    }
                }
            }
        }
    }
}
